<template>
  <div
    class="slide-to-confirm-container"
    :class="{ disabled: disabled, 'has-error': error }"
    :data-disable-swipe-navigation="!disabled ? 'true' : 'false'"
  >
    <div
      class="slide-track"
      ref="track"
      :class="{ loading: loading, disabled: disabled }"
      @touchstart="handleStart"
      @touchmove="handleMove"
      @touchend="handleEnd"
      @mousedown="handleMouseStart"
    >
      <!-- Track fill background - green appears when slide completes and user lets go -->
      <div
        class="track-fill"
        :class="{ 'is-sliding': isSliding }"
        :style="{ width: isConfirming || loading ? '100%' : '0%' }"
        v-if="isConfirming || loading"
      ></div>

      <!-- Brown overlay that covers text as handle slides -->
      <div
        class="brown-overlay"
        :class="{ 'is-sliding': isSliding }"
        :style="{ width: handlePosition + (handleWidth || 40) + 'px' }"
        v-if="!isConfirming && !loading && handlePosition > 0"
      ></div>

      <!-- Confirm text - only appears when user slides to end and lets go -->
      <div class="confirm-text" v-if="isConfirming || loading">
        <img
          src="/assets/icons/my-giving/lock-inactive.svg"
          alt="Lock inactive"
          class="confirm-icon"
        />
        <span class="confirm-text-label">{{ error ? 'Try again' : 'Processing...' }}</span>
      </div>

      <!-- Slide text - changes based on active/inactive state -->
      <div class="slide-text" :class="{ 'is-active': !disabled }" v-if="!loading && !isConfirming">
        <span class="slide-text-label">{{ error ? 'Try again' : text }}</span>
      </div>

      <!-- Lock icon - positioned on the right side, always use lock-inactive.svg -->
      <div class="lock-icon-container" v-if="!loading && !isConfirming">
        <img
          src="/assets/icons/my-giving/lock-inactive.svg"
          alt="Lock inactive"
          class="lock-icon"
        />
      </div>

      <!-- Handle/Thumb -->
      <div
        class="slide-handle"
        ref="handle"
        :style="{
          transform: `translate3d(${handlePosition}px, 0, 0)`,
          backgroundColor: handleBackgroundColor,
        }"
        :class="{
          'at-end': isAtEnd,
          'is-sliding': isSliding,
          shaking: error && !isSliding,
          'is-confirming': isConfirming,
          'disabled-handle': disabled,
        }"
      >
        <!-- Double chevron icon -->
        <img
          v-if="!isConfirming"
          :src="
            disabled
              ? '/assets/icons/my-giving/doublerightchevrons-white.svg'
              : '/assets/icons/my-giving/doublerightchevrons-black.svg'
          "
          alt="Chevrons"
          class="chevron-icon"
        />
        <!-- Loading spinner icon -->
        <div v-if="isConfirming" class="handle-spinner"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SlideToConfirm',
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    text: {
      type: String,
      default: 'Slide to Pay',
    },
  },
  data() {
    return {
      isSliding: false,
      startX: 0,
      handlePosition: 0,
      trackWidth: 0,
      handleWidth: 40, // Updated to match Figma design (40px handle)
      threshold: 0.95,
      maxPosition: 0,
      isAtEnd: false,
      isConfirming: false,
      boundHandleMouseMove: null,
      boundHandleMouseEnd: null,
    };
  },
  computed: {
    fillPercentage() {
      if (!this.trackWidth) return 0;
      const handleWidth = this.handleWidth || 40;
      // Fill should extend to cover the handle's right edge
      const handleRightEdge = this.handlePosition + handleWidth;
      return Math.min(100, Math.max(0, (handleRightEdge / this.trackWidth) * 100));
    },
    confirmOpacity() {
      if (this.fillPercentage < 50) return 0;
      if (this.fillPercentage >= 70) return 1;
      return (this.fillPercentage - 50) * 0.05;
    },
    handleBackgroundColor() {
      if (this.disabled) return '#a5a4a3';
      if (this.isConfirming || this.loading) return '#ffffff';

      // Calculate slide progress (0 to 1)
      const progress =
        this.maxPosition > 0 ? Math.min(1, this.handlePosition / this.maxPosition) : 0;

      // Interpolate from yellow (#fec629) to white (#ffffff) based on progress
      if (progress === 0) return '#fec629';
      if (progress >= 1) return '#ffffff';

      // Linear interpolation between yellow and white
      const yellow = { r: 254, g: 198, b: 41 }; // #fec629
      const white = { r: 255, g: 255, b: 255 }; // #ffffff

      const r = Math.round(yellow.r + (white.r - yellow.r) * progress);
      const g = Math.round(yellow.g + (white.g - yellow.g) * progress);
      const b = Math.round(yellow.b + (white.b - yellow.b) * progress);

      return `rgb(${r}, ${g}, ${b})`;
    },
  },
  watch: {
    error(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.resetHandle();
      }
    },
    loading(newVal, oldVal) {
      if (newVal) {
        this.isAtEnd = true;
        if (this.handlePosition < this.maxPosition) {
          this.handlePosition = this.maxPosition;
        }
        // Keep spinner visible in handle when loading
        this.isConfirming = true;
      } else if (!newVal && oldVal && this.error && !this.isSliding) {
        this.resetHandle();
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.calculateDimensions();
    });
    window.addEventListener('resize', this.calculateDimensions);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.calculateDimensions);
    // Clean up mouse event listeners
    this.removeMouseListeners();
  },
  methods: {
    calculateDimensions() {
      this.$nextTick(() => {
        if (this.$refs.track && this.$refs.handle) {
          const trackRect = this.$refs.track.getBoundingClientRect();
          this.trackWidth = trackRect.width;
          this.handleWidth = this.$refs.handle.offsetWidth;
          // Track has padding: 8px left, 20px right
          // Handle starts at left: 8px (matching left padding)
          // Handle width: 40px
          // Max handle left edge position: trackWidth - 20px (right padding) - 40px (handle width) = trackWidth - 60px
          // Max translate: (trackWidth - 60px) - 8px (initial position) = trackWidth - 68px
          this.maxPosition = this.trackWidth - this.handleWidth - 20 - 8;
        }
      });
    },
    getEventX(event) {
      if (event.touches && event.touches.length > 0) {
        return event.touches[0].clientX;
      }
      return event.clientX;
    },
    handleStart(event) {
      // Block new slide gestures while a confirmation is already in flight.
      // isConfirming stays true until parent resets via loading→false (no error)
      // or until resetHandle on error retry.
      if (this.disabled || this.loading || this.isConfirming) return;

      if (!this.maxPosition || !this.trackWidth) {
        this.calculateDimensions();
      }

      if (this.error) {
        this.$emit('clear-error');
        this.resetHandle();
      }

      this.isSliding = true;
      const trackRect = this.$refs.track.getBoundingClientRect();
      // Account for 8px left padding when calculating start position
      this.startX = this.getEventX(event) - trackRect.left - 8;

      event.preventDefault();
      event.stopPropagation();
    },
    handleMouseStart(event) {
      // Handle mouse events for desktop browsers
      this.handleStart(event);
      // Add document-level listeners for mouse move and up
      // Bind methods to ensure 'this' context is correct
      this.boundHandleMouseMove = this.handleMouseMove.bind(this);
      this.boundHandleMouseEnd = this.handleMouseEnd.bind(this);
      document.addEventListener('mousemove', this.boundHandleMouseMove);
      document.addEventListener('mouseup', this.boundHandleMouseEnd);
    },
    handleMouseMove(event) {
      // Use the same handleMove logic for mouse events
      this.handleMove(event);
    },
    handleMouseEnd(event) {
      // Use the same handleEnd logic for mouse events
      this.handleEnd(event);
      // Remove document-level listeners
      this.removeMouseListeners();
    },
    removeMouseListeners() {
      if (this.boundHandleMouseMove) {
        document.removeEventListener('mousemove', this.boundHandleMouseMove);
      }
      if (this.boundHandleMouseEnd) {
        document.removeEventListener('mouseup', this.boundHandleMouseEnd);
      }
      this.boundHandleMouseMove = null;
      this.boundHandleMouseEnd = null;
    },
    handleMove(event) {
      if (!this.isSliding || this.disabled || this.loading) return;

      event.preventDefault();
      event.stopPropagation();

      const trackRect = this.$refs.track.getBoundingClientRect();
      // Account for 8px left padding when calculating position
      const deltaX = this.getEventX(event) - trackRect.left - 8 - this.startX;
      const newPosition = Math.max(0, Math.min(this.maxPosition, deltaX));

      this.handlePosition = newPosition;
      // Set isAtEnd when user reaches threshold - this makes handle turn white
      this.isAtEnd = this.maxPosition > 0 && newPosition / this.maxPosition >= this.threshold;
    },
    handleEnd(event) {
      if (!this.isSliding || this.disabled || this.loading) return;

      this.isSliding = false;

      // Only show processing state if user reached threshold AND released
      if (this.maxPosition > 0 && this.handlePosition / this.maxPosition >= this.threshold) {
        this.isAtEnd = true;
        this.handlePosition = this.maxPosition;
        // Set confirming state - this triggers processing UI
        this.isConfirming = true;
        this.triggerHaptic();
        setTimeout(() => this.$emit('confirm'), 200);
      } else {
        this.snapBack();
      }
    },
    triggerHaptic() {
      // Use Vibration API for haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate(10); // Short vibration (10ms)
      }
    },
    snapBack() {
      this.isAtEnd = false;
      this.handlePosition = 0;
      // Clean up mouse listeners if dragging with mouse
      this.removeMouseListeners();
    },
    resetHandle() {
      this.isSliding = false;
      this.isAtEnd = false;
      this.isConfirming = false;
      this.handlePosition = 0;
      this.startX = 0;
      // Clean up mouse listeners
      this.removeMouseListeners();
    },
  },
};
</script>

<style scoped>
.slide-to-confirm-container {
  width: 100%;
  height: 56px;
  position: relative;
}

@media (min-width: 768px) {
  .slide-to-confirm-container {
    height: 64px;
  }
}

@media (min-width: 1024px) {
  .slide-to-confirm-container {
    height: 72px;
  }
}

.slide-track {
  position: relative;
  width: 100%;
  height: 56px;
  background-color: #42362b;
  border-radius: 100px;
  overflow: hidden;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  touch-action: none;
  padding: 8px 20px 8px 8px;
  box-sizing: border-box;
}

@media (min-width: 768px) {
  .slide-track {
    height: 64px;
  }
}

@media (min-width: 1024px) {
  .slide-track {
    height: 72px;
  }
}

.slide-track.disabled {
  background-color: #c3c2c1;
  cursor: not-allowed;
  pointer-events: none;
}

.slide-to-confirm-container.disabled {
  opacity: 0.5;
}

.slide-track.loading {
  pointer-events: none;
}

.slide-track.has-error {
  animation: shake 0.5s ease-in-out;
}

.track-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background-color: #20bf55;
  border-radius: 100px;
  transition: width 0.3s ease-out;
  z-index: 2;
  will-change: width;
}

.track-fill.is-sliding {
  transition: none;
}

.brown-overlay {
  position: absolute;
  top: 0;
  left: 8px;
  height: 100%;
  background-color: #42362b;
  border-radius: 100px;
  z-index: 2;
  pointer-events: none;
  transition: width 0.1s ease-out;
}

.brown-overlay.is-sliding {
  transition: none;
}

.confirm-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
  z-index: 3;
  transition: opacity 0.1s ease;
}

@media (min-width: 768px) {
  .confirm-text {
    font-size: 22px;
  }
}

@media (min-width: 1024px) {
  .confirm-text {
    font-size: 24px;
  }
}

.confirm-text-label {
  color: #ffffff;
}

.confirm-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.slide-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  z-index: 1;
  pointer-events: none;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: -0.48px;
  white-space: nowrap;
}

@media (min-width: 768px) {
  .slide-text {
    font-size: 20px;
    line-height: 28px;
  }
}

@media (min-width: 1024px) {
  .slide-text {
    font-size: 22px;
    line-height: 30px;
  }
}

.slide-text-label {
  color: #ffffff;
  text-align: center;
}

.slide-text.is-active .slide-text-label {
  color: #ffffff;
}

.lock-icon-container {
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  z-index: 1;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Lock icon will be covered by handle as it slides */
}

.lock-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.loading-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 18px;
  font-weight: 600;
  color: #21201e;
  z-index: 2;
  pointer-events: none;
  white-space: nowrap;
}

.slide-handle {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 40px;
  height: 40px;
  background-color: #fec629;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 3;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: grab;
  touch-action: none;
  will-change: transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  padding: 7.5px;
  box-sizing: border-box;
}

@media (min-width: 768px) {
  .slide-handle {
    width: 48px;
    height: 48px;
    top: 8px;
  }
}

@media (min-width: 1024px) {
  .slide-handle {
    width: 56px;
    height: 56px;
    top: 8px;
  }
}

.slide-handle.disabled-handle {
  background-color: #a5a4a3;
}

.slide-handle:active {
  cursor: grabbing;
}

.slide-handle.at-end {
  transition: transform 0.2s ease-out;
}

.slide-handle.shaking {
  animation: shake 0.5s ease-in-out;
}

.chevron-icon {
  width: 25px;
  height: 25px;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .chevron-icon {
    width: 28px;
    height: 28px;
  }
}

@media (min-width: 1024px) {
  .chevron-icon {
    width: 32px;
    height: 32px;
  }
}

.slide-handle.is-confirming .chevron-icon {
  opacity: 0;
}

.handle-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(66, 54, 43, 0.2);
  border-top-color: #42362b;
  border-radius: 50%;
  animation: handleSpin 0.6s linear infinite;
}

@keyframes handleSpin {
  to {
    transform: rotate(360deg);
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4;
  background-color: rgba(66, 54, 43, 0.1);
  border-radius: 100px;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-4px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(4px);
  }
}

.slide-handle.is-sliding {
  transition: none !important;
}

.slide-handle:not(.is-sliding):not(.at-end) {
  transition: transform 0.3s ease-out;
}
</style>
